/**
 * useReportsPage
 *
 * The single hook every reports list page calls. It wires a page into the
 * shared ReportsContext (the only source of fetching and caching) and owns the
 * per-page plumbing:
 *
 *   1. URL <-> context state synchronisation (seed from query params on mount,
 *      write state back to the URL as it changes).
 *   2. Fetch orchestration: a single idempotent effect that drives
 *      `context.fetchPage` whenever the real query inputs change, with a
 *      signature guard so returning from the report detail page (which already
 *      holds matching cached data) does not trigger a reload.
 *
 * Mirrors useJournalArticlesPage without the PR Bulk Review options (locked
 * view mode, default sort, score slider, quick filters). URL parameter names
 * are the same as Journal Articles: `search`, `view`, `columns`, `page`,
 * `pageSize`, and `filter_<key>`.
 */

import { useCallback, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { useReports, ReportsContextValue } from "@/components/reports/ReportsContext"
import { ReportsSidebarOption } from "@/components/reports/reportsRoutes"
import { getColumnGroupsForViewMode } from "@/components/reports/columns"
import { ViewMode } from "@/components/shared/TableViewSelector"
import {
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
} from "@/components/journal-articles/useJournalArticlesPage"

/** View modes that are valid to seed from / write to the URL `view` param. */
const VALID_VIEW_MODES: ViewMode[] = ["compact", "status", "full"]

/** Prefix of the URL query params that carry table filters. */
const FILTER_PARAM_PREFIX: string = "filter_"

/**
 * Get the default visible column keys for a view mode.
 *
 * @param viewMode - A view mode the reports table supports.
 * @returns Every column key in that view's column groups.
 */
function defaultColumnKeysForViewMode(viewMode: ViewMode): string[] {
  return getColumnGroupsForViewMode(viewMode).flatMap((g) => g.keys)
}

/**
 * Options accepted by {@link useReportsPage}.
 */
export interface UseReportsPageOptions {
  /**
   * The sidebar option this page represents. The hook pushes it into the
   * context on mount and whenever it changes, so the context's `fetchPage`
   * builds the matching `reportType` / `createdAfter` filter.
   */
  sidebarOption: ReportsSidebarOption
}

/**
 * The value returned by {@link useReportsPage}: the full context value plus
 * pagination and filter handlers.
 */
export interface ReportsPage extends ReportsContextValue {
  /** Go to the next page of results (no-op on the last page). */
  goToNextPage: () => void
  /** Go to the previous page of results (no-op on the first page). */
  goToPreviousPage: () => void
  /** Go to the first page (no-op if already there). */
  goToFirstPage: () => void
  /** Go to the last page (no-op if already there). */
  goToLastPage: () => void
  /**
   * Change the number of rows per page and reset to the first page. Ignores
   * values outside {@link PAGE_SIZE_OPTIONS}.
   *
   * @param value - The selected page size as a string (from a Select control).
   */
  changePageSize: (value: string) => void
  /**
   * Apply a new filter map and clear any open details panel.
   *
   * @param filters - The new active filter map.
   */
  changeFilters: (filters: Map<string, Set<string>>) => void
}

/**
 * Wire a reports page into the shared context, syncing URL state and
 * orchestrating fetches.
 *
 * @param options - The page's fixed sidebar option.
 * @returns The context value augmented with page handlers.
 */
export function useReportsPage(options: UseReportsPageOptions): ReportsPage {
  const { sidebarOption } = options
  const [searchParams, setSearchParams] = useSearchParams()
  const context: ReportsContextValue = useReports()

  const {
    reports,
    totalPages,
    pageIndex,
    pageSize,
    sidebarOption: contextSidebarOption,
    searchQuery,
    activeFilters,
    sortConfig,
    viewMode,
    visibleColumns,
    lastFetchTime,
    fetchPage,
    setPageIndex,
    setPageSize,
    setSidebarOption,
    setSearchQuery,
    setActiveFilters,
    setViewMode,
    setVisibleColumns,
    clearSelectedReport,
  } = context

  // Guards so URL params are only applied to context once, on initial mount.
  const initialLoadDoneRef = useRef<boolean>(false)
  const prevSidebarOptionRef = useRef<ReportsSidebarOption>(sidebarOption)

  // ---------------------------------------------------------------------------
  // Keep the context's sidebar option aligned with this page's fixed option.
  //
  // The initial mount is handled by the seed effect below; this fires only when
  // the page's option changes afterwards (e.g. ReportTypePage switching between
  // type slugs without remounting).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      return
    }
    if (sidebarOption !== prevSidebarOptionRef.current) {
      prevSidebarOptionRef.current = sidebarOption
      setSidebarOption(sidebarOption)
    }
  }, [sidebarOption, setSidebarOption])

  // ---------------------------------------------------------------------------
  // Initial mount: seed context state from URL query params, then let the fetch
  // effect below drive the first fetch.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      return
    }
    initialLoadDoneRef.current = true

    // Seed the sidebar option before the first fetch so the fetch uses the
    // correct report type / date filter. setSidebarOption also clears the
    // search, so the URL search below is applied after it.
    if (sidebarOption !== contextSidebarOption) {
      setSidebarOption(sidebarOption)
    }
    prevSidebarOptionRef.current = sidebarOption

    // If the context already holds fetched data, this is a REMOUNT (e.g.
    // returning from the report detail page). Re-seeding from the URL would
    // install fresh Map/string references and trigger a reload, so the cached
    // state stands.
    if (lastFetchTime !== null) {
      return
    }

    // Page size. Values outside the offered options are ignored.
    const urlPageSize: string | null = searchParams.get("pageSize")
    if (urlPageSize) {
      const parsed: number = parseInt(urlPageSize, 10)
      if (PAGE_SIZE_OPTIONS.includes(parsed) && parsed !== pageSize) {
        setPageSize(parsed)
      }
    }

    // View mode. Only compact/status/full are honoured, so an unsupported value
    // (e.g. an article-only "pr-bulk-review") never reaches
    // getColumnGroupsForViewMode, which would throw.
    const urlView: string | null = searchParams.get("view")
    const seededViewMode: ViewMode =
      urlView !== null && VALID_VIEW_MODES.includes(urlView as ViewMode)
        ? (urlView as ViewMode)
        : viewMode
    if (seededViewMode !== viewMode) {
      setViewMode(seededViewMode)
    }

    // Search.
    const urlSearch: string | null = searchParams.get("search")
    if (urlSearch && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch)
    }

    // Visible columns, validated against the seeded view's column groups (a
    // subset of ALL_REPORT_COLUMN_KEYS). Without a valid `columns` param, a
    // seeded non-default view shows all of its own columns; otherwise the
    // context's compact defaults would be applied to the status/full view.
    const viewColumnKeys: string[] = defaultColumnKeysForViewMode(seededViewMode)
    const urlColumns: string | null = searchParams.get("columns")
    const urlColumnKeys: string[] = urlColumns
      ? urlColumns.split(",").filter((k: string): boolean => viewColumnKeys.includes(k))
      : []
    if (urlColumnKeys.length > 0) {
      setVisibleColumns(new Set(urlColumnKeys))
    } else if (seededViewMode !== viewMode) {
      setVisibleColumns(new Set(viewColumnKeys))
    }

    // Filters. Every `filter_<key>` param is seeded as-is. Only
    // prExtractionStatus and rsExtractionStatus are valid; any other key or an
    // unknown label (e.g. from a pasted article URL) makes buildReportsFilters
    // throw, which the context surfaces as the table error, rather than the
    // filter being silently dropped.
    const urlFilters: Map<string, Set<string>> = new Map()
    searchParams.forEach((value: string, key: string): void => {
      if (key.startsWith(FILTER_PARAM_PREFIX) && value) {
        const filterKey: string = key.slice(FILTER_PARAM_PREFIX.length)
        urlFilters.set(filterKey, new Set(value.split(",").map(decodeURIComponent)))
      }
    })
    if (urlFilters.size > 0) {
      setActiveFilters(urlFilters)
    }

    // Page index (1-based in URL, 0-based internally). Applied last, because
    // setPageSize and setActiveFilters both reset the page to 0.
    const urlPage: string | null = searchParams.get("page")
    if (urlPage) {
      const parsed: number = parseInt(urlPage, 10)
      if (!isNaN(parsed) && parsed >= 1) {
        setPageIndex(parsed - 1)
      }
    }
    // Deliberately runs once on mount: it reads the URL and the context state
    // as they are at mount time. Re-running when those values change would
    // overwrite the user's later changes with the original URL params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------------
  // Fetch orchestration. This effect is the only place that triggers a fetch.
  // A signature of the real query inputs guards it, so StrictMode
  // double-invokes, re-runs caused by unrelated dependencies (reports,
  // lastFetchTime), and returning from the detail page with matching cached
  // data do not refetch.
  // ---------------------------------------------------------------------------
  const lastFetchSignatureRef = useRef<string | null>(null)

  /**
   * Build a string that identifies the request for a given page.
   *
   * @param page - The zero-based page index.
   * @returns A signature covering sidebar option, search, page size, page,
   *   sort, and filters.
   */
  const buildFetchSignature = useCallback(
    (page: number): string => {
      const filtersPart: string = [...activeFilters.entries()]
        .map(([key, values]: [string, Set<string>]): string => `${key}:${[...values].sort().join("|")}`)
        .sort()
        .join(";")
      const sortPart: string = sortConfig ? `${sortConfig.key}:${sortConfig.direction}` : "none"
      return [contextSidebarOption, searchQuery, pageSize, page, sortPart, filtersPart].join("~")
    },
    [contextSidebarOption, searchQuery, pageSize, sortConfig, activeFilters]
  )

  useEffect(() => {
    // On the very first run, adopt cached context data (a return from the
    // detail page) as already fetched, so it is not reloaded.
    if (lastFetchSignatureRef.current === null) {
      const hasCachedData: boolean =
        lastFetchTime !== null && reports.length > 0 && contextSidebarOption === sidebarOption
      if (hasCachedData) {
        lastFetchSignatureRef.current = buildFetchSignature(pageIndex)
        return
      }
    }

    const signature: string = buildFetchSignature(pageIndex)
    if (signature === lastFetchSignatureRef.current) {
      return
    }

    lastFetchSignatureRef.current = signature
    // fetchPage stores its result (or error) in context state; nothing to await here.
    void fetchPage(pageIndex)
  }, [
    lastFetchTime,
    reports,
    contextSidebarOption,
    sidebarOption,
    pageIndex,
    buildFetchSignature,
    fetchPage,
  ])

  // ---------------------------------------------------------------------------
  // Sync context state back to the URL query params. Defaults are omitted to
  // keep URLs short.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params: Record<string, string> = {}

    if (viewMode !== "compact") {
      params.view = viewMode
    }

    if (searchQuery) {
      params.search = searchQuery
    }

    // Persist columns only when some of the current view's columns are hidden.
    const currentDefaultKeys: string[] = defaultColumnKeysForViewMode(viewMode)
    const allCurrentVisible: boolean = currentDefaultKeys.every((k: string): boolean => visibleColumns.has(k))
    if (!allCurrentVisible) {
      const relevantVisible: string[] = [...visibleColumns].filter((k: string): boolean =>
        currentDefaultKeys.includes(k)
      )
      if (relevantVisible.length > 0) {
        params.columns = relevantVisible.join(",")
      }
    }

    if (pageIndex > 0) {
      params.page = String(pageIndex + 1)
    }

    if (pageSize !== DEFAULT_PAGE_SIZE) {
      params.pageSize = String(pageSize)
    }

    activeFilters.forEach((values: Set<string>, key: string): void => {
      if (values.size > 0) {
        params[`${FILTER_PARAM_PREFIX}${key}`] = [...values].join(",")
      }
    })

    setSearchParams(params, { replace: true })
  }, [viewMode, visibleColumns, pageIndex, pageSize, searchQuery, activeFilters, setSearchParams])

  // ---------------------------------------------------------------------------
  // Pagination and filter handlers. Each closes the details panel so a stale
  // selection does not linger across pages.
  // ---------------------------------------------------------------------------

  /** Go to the next page (no-op on the last page). */
  const goToNextPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    setPageIndex(pageIndex + 1)
    clearSelectedReport()
  }, [pageIndex, totalPages, setPageIndex, clearSelectedReport])

  /** Go to the previous page (no-op on the first page). */
  const goToPreviousPage = useCallback((): void => {
    if (pageIndex === 0) return
    setPageIndex(pageIndex - 1)
    clearSelectedReport()
  }, [pageIndex, setPageIndex, clearSelectedReport])

  /** Go to the first page (no-op if already there). */
  const goToFirstPage = useCallback((): void => {
    if (pageIndex === 0) return
    setPageIndex(0)
    clearSelectedReport()
  }, [pageIndex, setPageIndex, clearSelectedReport])

  /** Go to the last page (no-op if already there). */
  const goToLastPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    setPageIndex(totalPages - 1)
    clearSelectedReport()
  }, [pageIndex, totalPages, setPageIndex, clearSelectedReport])

  /**
   * Change the page size. The context's setPageSize already resets to the
   * first page and closes the details panel.
   *
   * @param value - The selected page size as a string (from a Select control).
   */
  const changePageSize = useCallback(
    (value: string): void => {
      const parsed: number = parseInt(value, 10)
      if (!PAGE_SIZE_OPTIONS.includes(parsed)) return
      setPageSize(parsed)
    },
    [setPageSize]
  )

  /**
   * Apply a new filter map. The context's setActiveFilters already resets to
   * the first page and closes the details panel.
   *
   * @param filters - The new active filter map.
   */
  const changeFilters = useCallback(
    (filters: Map<string, Set<string>>): void => {
      setActiveFilters(filters)
    },
    [setActiveFilters]
  )

  return {
    ...context,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    changePageSize,
    changeFilters,
  }
}
